// src/pages/contrats/NouveauContrat.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Card, Row, Col, Form, Button, Alert, Spinner,
    InputGroup, Badge, ProgressBar, Modal
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   ÉTAPES DU WIZARD
   ============================================================ */
const STEPS = [
    { id: 1, key: 'type',         label: 'Type',          icon: 'ri-file-list-3-line',      desc: 'Nature du document' },
    { id: 2, key: 'employeur',    label: 'Employeur',     icon: 'ri-building-line',         desc: 'CMS & représentant' },
    { id: 3, key: 'employe',      label: 'Employé',       icon: 'ri-user-line',             desc: 'État civil complet' },
    { id: 4, key: 'poste',        label: 'Poste & Client',icon: 'ri-briefcase-line',        desc: 'Qualification & client' },
    { id: 5, key: 'periode',      label: 'Période',       icon: 'ri-calendar-line',         desc: 'Dates & durée' },
    { id: 6, key: 'remuneration', label: 'Rémunération',  icon: 'ri-money-cfa-circle-line', desc: 'Salaire & primes' },
    { id: 7, key: 'clauses',      label: 'Clauses',       icon: 'ri-shield-check-line',     desc: 'Fautes & engagements' },
    { id: 8, key: 'documents',    label: 'Documents',     icon: 'ri-attachment-line',       desc: 'Pièces jointes' },
    { id: 9, key: 'recap',        label: 'Récapitulatif', icon: 'ri-check-double-line',     desc: 'Vérification' }
];

/* ============================================================
   EMPLOYEUR CMS PAR DÉFAUT
   ============================================================ */
const DEFAULT_EMPLOYEUR = {
    raison_sociale: 'CONSULTING MAXI SERVICES (C.M.S)',
    forme_juridique: 'SARL au capital de 2.000.000 F.CFA',
    activite: 'Prestation de services Métallurgie, Soudure, Tuyauterie & Chaudronnerie',
    adresse: 'B.P. 548 - Port-Gentil, Gabon',
    telephone: '01 56 09 42',
    telephone2: '07 35 48 80',
    email: 'cmsmangalaeliane@yahoo.fr',
    site_web: 'www.cms-gabon.com',
    rccm: 'RG/POG 2010 B 0582',
    nif: '799 747 M',
    cnss: '001 0028 603-M',
    cnamgs: '041500000733',
    ascoma: '8CP0400111',
    representant_nom: 'MABICKA Orphée Lorraine Epse MAKAKOU',
    representant_qualite: 'Gérante'
};

const DEFAULT_PRIMES = {
    salaire_base: 200000,
    prime_assiduite: 4000,
    prime_securite: 46000,
    prime_chantier: 300000,
    prime_mer: 175000,
    prime_rendement: 23000,
    prime_gaz: 12000,
    prime_transport: 35000,
    indemnite_diverse: 100000,
    prime_anciennete: 0
};

const DEFAULT_FAUTES = [
    'Insuffisance professionnelle',
    'Inadaptation en milieu du travail',
    'Usage de faux, contrebande, trucage des données & matériels',
    'Indisponibilité & absence non justifiée',
    'Non-respect scrupuleux des règles d\'hygiène',
    'Absorption d\'alcool, vol, refus de travail, indiscipline caractérisée',
    'Rixe, violence et voies de fait envers un collègue ou un chef hiérarchique'
];

const EMPTY_EMPLOYE = {
    nom: '', prenom: '', date_naissance: '', lieu_naissance: '',
    situation_familiale: 'Célibataire', nationalite: 'Gabonaise',
    nombre_enfants: 0, cnss: '', piece_identite: '',
    domicile: '', nom_pere: '', nom_mere: '',
    personne_prevenir: '', tel_prevenir: '', tel_personnel: '',
    email: '', telephone: '', adresse: '', date_entree: ''
};

/* ============================================================
   TOAST
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    useEffect(() => {
        if (show && autoHideDelay) {
            const t = setTimeout(onClose, autoHideDelay);
            return () => clearTimeout(t);
        }
    }, [show, autoHideDelay, onClose]);

    if (!show) return null;
    const cfg = {
        success: { bg: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bg: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bg: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bg: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bg: COLORS.info, icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: cfg.bg }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${cfg.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: cfg.bg, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   FIXED ALERT BAR
   ============================================================ */
const FixedAlertBar = ({ alerts, onClose }) => {
    if (!alerts?.length) return null;
    return (
        <div className="fixed-alert-bar">
            {alerts.map((a, i) => (
                <Alert key={i} variant={a.type} onClose={() => onClose(i)} dismissible className="alert-modern shadow-sm mb-2">
                    <div className="d-flex align-items-center">
                        <div className="alert-icon-wrapper"><i className={`${a.icon} fs-5`}></i></div>
                        <div className="ms-3">
                            <strong className="d-block">{a.title}</strong>
                            <span className="small text-muted">{a.message}</span>
                        </div>
                    </div>
                </Alert>
            ))}
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function NouveauContrat() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [currentStep, setCurrentStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [fixedAlerts, setFixedAlerts] = useState([]);
    const [clientFromUrl, setClientFromUrl] = useState(false); // ⭐ NOUVEAU

    /* --------- EMPLOYÉS --------- */
    const [employes, setEmployes] = useState([]);
    const [loadingEmployes, setLoadingEmployes] = useState(false);
    const [selectedEmployeId, setSelectedEmployeId] = useState(null);
    const [showEmployeModal, setShowEmployeModal] = useState(false);
    const [searchEmploye, setSearchEmploye] = useState('');
    const [employeMode, setEmployeMode] = useState('manual'); // 'existing' | 'manual'

    /* --------- CLIENTS --------- */
    const [clients, setClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState(null);

    /* --------- ÉTAT DU FORMULAIRE --------- */
    const [form, setForm] = useState({
        type_contrat: 'CDD',
        reference: '',
        contrat_initial_ref: '',
        contrat_initial_dates: '',

        // ⭐ Liens
        employe_id: null,
        id_client: null,

        employeur: { ...DEFAULT_EMPLOYEUR },
        employe: { ...EMPTY_EMPLOYE },

        poste: {
            qualification: 'Manutentionnaire',
            fonction: 'Manutentionnaire',
            departement: '',
            client: '',
            site: '',
            province: 'Ogooué Maritime (Port-Gentil)',
            missions: [
                'Tous les travaux se rapportant aux tâches de Manutentionnaire',
                'Assister les collègues dans l\'exécution des tâches assimilées'
            ]
        },

        periode: {
            date_debut: '',
            date_fin: '',
            duree_jours: 0,
            jours_site: 14,
            jours_sortie: 1,
            type_remuneration: 'site'
        },

        remuneration: { ...DEFAULT_PRIMES },

        clauses: {
            fautes: [...DEFAULT_FAUTES],
            conges_payes: 'Selon la législation en vigueur',
            clause_non_concurrence: true,
            soins_medicaux: true,
            penalites_preventives: true,
            observations: ''
        },

        documents: {
            lettre_fin_contrat: null,
            certificat_travail: null,
            piece_identite: null,
            acte_naissance: null,
            photo_identite: null
        }
    });

    /* --------- ALERTES --------- */
    useEffect(() => {
        if (fixedAlerts.length > 0) {
            const timers = fixedAlerts.map((_, i) =>
                setTimeout(() => setFixedAlerts(prev => prev.filter((_, idx) => idx !== i)), 8000)
            );
            return () => timers.forEach(t => clearTimeout(t));
        }
    }, [fixedAlerts]);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    const addAlert = useCallback((type, title, message) => {
        const icons = {
            success: 'ri-checkbox-circle-line',
            error:   'ri-error-warning-line',
            warning: 'ri-alert-line',
            info:    'ri-information-line'
        };
        setFixedAlerts(prev => [...prev, { type, title, message, icon: icons[type] || 'ri-information-line' }]);
    }, []);
    const removeAlert = (i) => setFixedAlerts(prev => prev.filter((_, idx) => idx !== i));

    /* --------- CHARGEMENT DES EMPLOYÉS --------- */
    const fetchEmployes = useCallback(async () => {
        setLoadingEmployes(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur chargement employés");
            const result = await response.json();
            if (result.success) {
                setEmployes(result.data || []);
            } else {
                addAlert('error', 'Erreur', result.message || 'Erreur de chargement des employés');
            }
        } catch (err) {
            console.error("Erreur chargement employés:", err);
            addAlert('error', 'Erreur', err.message);
        } finally {
            setLoadingEmployes(false);
        }
    }, [addAlert]);

    /* --------- CHARGEMENT DES CLIENTS --------- */
    const fetchClients = useCallback(async () => {
        setLoadingClients(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur chargement clients");
            const result = await response.json();
            if (result.data) setClients(result.data);
        } catch (err) {
            console.error("Erreur chargement clients:", err);
        } finally {
            setLoadingClients(false);
        }
    }, []);

    useEffect(() => {
        fetchEmployes();
        fetchClients();
    }, [fetchEmployes, fetchClients]);

    /* ============================================================
       ⭐ PRÉ-SÉLECTION DU CLIENT DEPUIS L'URL (?id_client=X)
       ============================================================ */
    useEffect(() => {
        const idClientFromUrl = searchParams.get('id_client');
        if (!idClientFromUrl || clients.length === 0) return;

        const clientId = parseInt(idClientFromUrl, 10);
        if (isNaN(clientId)) return;

        const client = clients.find(c => c.id_client === clientId);
        if (client && !selectedClientId) {
            setSelectedClientId(client.id_client);
            setClientFromUrl(true);
            setForm(prev => ({
                ...prev,
                id_client: client.id_client,
                poste: { ...prev.poste, client: client.raison_sociale }
            }));

            // Info visuelle
            addAlert('success', 'Client pré-sélectionné',
                `Le contrat sera créé pour "${client.raison_sociale}"`);

            // Nettoyer l'URL pour ne pas re-déclencher
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('id_client');
            setSearchParams(newParams, { replace: true });

            // Aller directement à l'étape 4 (poste & client)
            setTimeout(() => setCurrentStep(4), 300);
        }
    }, [clients, searchParams, selectedClientId, setSearchParams, addAlert]);

    /* --------- HELPERS --------- */
    const updateForm = (path, value) => {
        setForm(prev => {
            const keys = path.split('.');
            const next = { ...prev };
            let ref = next;
            for (let i = 0; i < keys.length - 1; i++) {
                ref[keys[i]] = { ...ref[keys[i]] };
                ref = ref[keys[i]];
            }
            ref[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        updateForm(name, type === 'checkbox' ? checked : value);
    };

    const handleFileChange = (e, field) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            addAlert('error', 'Fichier trop volumineux', 'Le fichier ne doit pas dépasser 5 Mo');
            return;
        }
        updateForm(`documents.${field}`, file);
        showToast('success', 'Fichier ajouté', `${file.name} a été chargé`);
    };

    /* --------- SÉLECTION D'UN EMPLOYÉ --------- */
    const handleSelectEmploye = (emp) => {
        setSelectedEmployeId(emp.id);
        setEmployeMode('existing');

        setForm(prev => ({
            ...prev,
            employe_id: emp.id,
            employe: {
                nom: emp.nom || '',
                prenom: emp.prenom || '',
                date_naissance: emp.date_naissance || '',
                lieu_naissance: emp.lieu_naissance || '',
                situation_familiale: emp.situation_matrimoniale || 'Célibataire',
                nationalite: emp.nationalite || 'Gabonaise',
                nombre_enfants: emp.nombre_enfants || 0,
                cnss: emp.cnss || emp.numero_cnss || '',
                piece_identite: emp.piece_identite || '',
                domicile: emp.domicile || emp.adresse || '',
                nom_pere: emp.nom_pere || '',
                nom_mere: emp.nom_mere || '',
                personne_prevenir: emp.personne_prevenir || '',
                tel_prevenir: emp.tel_prevenir || '',
                tel_personnel: emp.telephone || emp.tel_personnel || '',
                email: emp.email || '',
                telephone: emp.telephone || '',
                adresse: emp.adresse || '',
                date_entree: emp.date_embauche || emp.date_entree || ''
            },
            poste: {
                ...prev.poste,
                qualification: emp.poste || prev.poste.qualification,
                fonction: emp.poste || prev.poste.fonction,
                departement: emp.departement || prev.poste.departement
            }
        }));

        showToast('success', 'Employé sélectionné', `${emp.prenom} ${emp.nom} a été chargé`);
        setShowEmployeModal(false);
        setSearchEmploye('');
    };

    const handleClearEmploye = () => {
        setSelectedEmployeId(null);
        setForm(prev => ({
            ...prev,
            employe_id: null,
            employe: { ...EMPTY_EMPLOYE }
        }));
        setEmployeMode('manual');
    };

    /* --------- SÉLECTION D'UN CLIENT (via le select) --------- */
    const handleClientChange = (e) => {
        const selectedId = e.target.value;
        if (!selectedId) {
            setSelectedClientId(null);
            setClientFromUrl(false);
            setForm(prev => ({
                ...prev,
                id_client: null,
                poste: { ...prev.poste, client: '' }
            }));
            return;
        }
        const clientId = parseInt(selectedId, 10);
        const client = clients.find(c => c.id_client === clientId);
        if (client) {
            setSelectedClientId(client.id_client);
            setClientFromUrl(false);
            setForm(prev => ({
                ...prev,
                id_client: client.id_client,
                poste: { ...prev.poste, client: client.raison_sociale }
            }));
            showToast('success', 'Client sélectionné', client.raison_sociale);
        }
    };

    /* --------- DÉTACHER LE CLIENT --------- */
    const handleClearClient = () => {
        setSelectedClientId(null);
        setClientFromUrl(false);
        setForm(prev => ({
            ...prev,
            id_client: null,
            poste: { ...prev.poste, client: '' }
        }));
    };

    /* --------- FILTRE EMPLOYÉS (modale) --------- */
    const filteredEmployes = useMemo(() => {
        if (!searchEmploye) return employes;
        const s = searchEmploye.toLowerCase();
        return employes.filter(emp =>
            (emp.nom || '').toLowerCase().includes(s) ||
            (emp.prenom || '').toLowerCase().includes(s) ||
            (emp.email || '').toLowerCase().includes(s) ||
            (emp.matricule || '').toLowerCase().includes(s) ||
            (emp.poste || '').toLowerCase().includes(s)
        );
    }, [employes, searchEmploye]);

    /* --------- CALCUL DURÉE --------- */
    useEffect(() => {
        const { date_debut, date_fin } = form.periode;
        if (date_debut && date_fin) {
            const d1 = new Date(date_debut);
            const d2 = new Date(date_fin);
            const diff = Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
            if (diff > 0) updateForm('periode.duree_jours', diff);
        }
    }, [form.periode.date_debut, form.periode.date_fin]);

    /* --------- TOTAL RÉMUNÉRATION --------- */
    const totalRemuneration = useMemo(() => {
        return Object.values(form.remuneration).reduce((sum, v) => sum + (Number(v) || 0), 0);
    }, [form.remuneration]);

    /* --------- VALIDATION --------- */
    const validateStep = (step) => {
        const errors = [];
        if (step === 1) {
            if (!form.type_contrat) errors.push('Le type de document est requis');
            if (form.type_contrat === 'AVENANT' && !form.contrat_initial_ref)
                errors.push('La référence du contrat initial est requise pour un avenant');
        }
        if (step === 2) {
            if (!form.employeur.raison_sociale) errors.push('La raison sociale est requise');
            if (!form.employeur.representant_nom) errors.push('Le nom du représentant est requis');
        }
        if (step === 3) {
            if (!form.employe.nom) errors.push('Le nom de l\'employé est requis');
            if (!form.employe.prenom) errors.push('Le prénom de l\'employé est requis');
            if (!form.employe.date_naissance) errors.push('La date de naissance est requise');
        }
        if (step === 4) {
            if (!form.poste.qualification) errors.push('La qualification est requise');
            if (!form.poste.fonction) errors.push('La fonction est requise');
            if (!form.id_client) errors.push('Le client / donneur d\'ordre est requis');
        }
        if (step === 5) {
            if (!form.periode.date_debut) errors.push('La date de début est requise');
            if (form.type_contrat === 'CDD' && !form.periode.date_fin)
                errors.push('La date de fin est requise pour un CDD');
            if (form.periode.date_debut && form.periode.date_fin &&
                new Date(form.periode.date_fin) < new Date(form.periode.date_debut))
                errors.push('La date de fin doit être postérieure à la date de début');
        }
        if (step === 6) {
            if (!form.remuneration.salaire_base || form.remuneration.salaire_base <= 0)
                errors.push('Le salaire de base est requis');
        }
        return errors;
    };

    const goNext = () => {
        const errors = validateStep(currentStep);
        if (errors.length > 0) {
            errors.forEach(e => addAlert('error', 'Validation', e));
            return;
        }
        setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goPrev = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goToStep = (step) => {
        if (step <= currentStep) {
            setCurrentStep(step);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    /* --------- SOUMISSION --------- */
    const handleSubmit = async (e) => {
        e.preventDefault();

        for (let s = 1; s <= STEPS.length - 1; s++) {
            const errs = validateStep(s);
            if (errs.length) {
                setCurrentStep(s);
                errs.forEach(er => addAlert('error', `Étape ${s}`, er));
                return;
            }
        }

        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const fd = new FormData();

            if (form.employe_id) fd.append('employe_id', form.employe_id);
            if (form.id_client)  fd.append('id_client', form.id_client);

            fd.append('type_contrat', form.type_contrat || '');
            fd.append('reference', form.reference || '');
            fd.append('contrat_initial_ref', form.contrat_initial_ref || '');
            fd.append('contrat_initial_dates', form.contrat_initial_dates || '');

            // Employeur (CMS)
            fd.append('employeur_nom', form.employeur.raison_sociale || '');
            fd.append('employeur_adresse', form.employeur.adresse || '');
            fd.append('employeur_email', form.employeur.email || '');
            fd.append('employeur_telephone', form.employeur.telephone || '');

            // Employé
            fd.append('employe_nom', form.employe.nom || '');
            fd.append('employe_prenom', form.employe.prenom || '');
            fd.append('employe_email', form.employe.email || '');
            fd.append('employe_telephone', form.employe.tel_personnel || form.employe.telephone || '');
            fd.append('employe_adresse', form.employe.domicile || form.employe.adresse || '');

            // Poste + Client
            fd.append('poste_qualification', form.poste.qualification || '');
            fd.append('poste_fonction', form.poste.fonction || '');
            fd.append('poste_departement', form.poste.departement || '');
            fd.append('poste_site', form.poste.site || '');
            fd.append('poste_province', form.poste.province || '');
            fd.append('poste_missions', JSON.stringify(form.poste.missions || []));
            fd.append('poste_client_nom', form.poste.client || '');

            // Période
            fd.append('date_debut', form.periode.date_debut || '');
            fd.append('date_fin', form.periode.date_fin || '');
            fd.append('jours_site', form.periode.jours_site || 0);
            fd.append('jours_sortie', form.periode.jours_sortie || 0);
            fd.append('type_remuneration', form.periode.type_remuneration || 'site');

            // JSON
            fd.append('remuneration_json', JSON.stringify(form.remuneration || {}));
            fd.append('clauses_json', JSON.stringify(form.clauses || {}));

            // Fichiers
            if (form.documents.lettre_fin_contrat) fd.append('lettre_fin_contrat', form.documents.lettre_fin_contrat);
            if (form.documents.certificat_travail)  fd.append('certificat_travail', form.documents.certificat_travail);

            fd.append('statut', 'actif');

            const res = await fetch(`${API_BASE_URL}/contrats.php`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();

            if (data.success) {
                showToast('success', 'Contrat créé', `Contrat #${data.id} enregistré avec succès`);
                addAlert('success', 'Succès', 'Redirection en cours...');
                setTimeout(() => {
                    if (form.id_client) {
                        navigate(`/ventes/clients/${form.id_client}`);
                    } else {
                        navigate('/rh/contrats');
                    }
                }, 2000);
            } else {
                addAlert('error', 'Erreur', data.message || 'Erreur lors de la création');
            }
        } catch (err) {
            console.error(err);
            addAlert('error', 'Erreur de connexion', err.message);
        } finally {
            setSaving(false);
        }
    };

    /* --------- HELPERS AFFICHAGE --------- */
    const getFullName = () =>
        `${form.employe.prenom} ${form.employe.nom}`.trim() || 'Nouvel employé';
    const getTypeContrat = () => form.type_contrat;
    const progress = ((currentStep - 1) / (STEPS.length - 1)) * 100;

    /* ============================================================
       RENDU DES ÉTAPES
       ============================================================ */

    /* ----- ÉTAPE 1 : TYPE ----- */
    const renderStep1 = () => (
        <Row className="g-4">
            <Col md={12}>
                <div className="section-title">
                    <span className="section-icon"><i className="ri-file-list-3-line"></i></span>
                    <h6>Nature du document</h6>
                </div>
            </Col>
            <Col md={12}>
                <div className="type-selector">
                    {[
                        { value: 'CDD',        icon: 'ri-file-text-line',       title: 'Contrat CDD',          desc: 'Contrat à durée déterminée' },
                        { value: 'CDI',        icon: 'ri-file-shield-2-line',   title: 'Contrat CDI',          desc: 'Contrat à durée indéterminée' },
                        { value: 'AVENANT',    icon: 'ri-file-add-line',        title: 'Avenant',              desc: 'Renouvellement / modification' },
                        { value: 'CERTIFICAT', icon: 'ri-award-line',           title: 'Certificat de travail', desc: 'Fin de collaboration' }
                    ].map(opt => (
                        <div
                            key={opt.value}
                            className={`type-card ${form.type_contrat === opt.value ? 'active' : ''}`}
                            onClick={() => updateForm('type_contrat', opt.value)}
                        >
                            <div className="type-icon"><i className={opt.icon}></i></div>
                            <div className="type-title">{opt.title}</div>
                            <div className="type-desc">{opt.desc}</div>
                            {form.type_contrat === opt.value && (
                                <div className="type-check"><i className="ri-check-line"></i></div>
                            )}
                        </div>
                    ))}
                </div>
            </Col>

            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Référence interne</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-hashtag"></i></InputGroup.Text>
                        <Form.Control
                            name="reference"
                            value={form.reference}
                            onChange={handleChange}
                            placeholder="Ex: CMS-2024-001"
                        />
                    </InputGroup>
                </Form.Group>
            </Col>

            {form.type_contrat === 'AVENANT' && (
                <>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label className="form-label-modern">
                                Référence contrat initial <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup className="input-modern">
                                <InputGroup.Text><i className="ri-link"></i></InputGroup.Text>
                                <Form.Control
                                    name="contrat_initial_ref"
                                    value={form.contrat_initial_ref}
                                    onChange={handleChange}
                                    placeholder="Ex: AVENANT N°001"
                                />
                            </InputGroup>
                        </Form.Group>
                    </Col>
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label className="form-label-modern">Période du contrat initial</Form.Label>
                            <InputGroup className="input-modern">
                                <InputGroup.Text><i className="ri-calendar-2-line"></i></InputGroup.Text>
                                <Form.Control
                                    name="contrat_initial_dates"
                                    value={form.contrat_initial_dates}
                                    onChange={handleChange}
                                    placeholder="Ex: du 04/10 au 24/10/2023"
                                />
                            </InputGroup>
                        </Form.Group>
                    </Col>
                </>
            )}
        </Row>
    );

    /* ----- ÉTAPE 2 : EMPLOYEUR CMS (fixe) ----- */
    const renderStep2 = () => (
        <Row className="g-4">
            <Col md={12}>
                <div className="section-title">
                    <span className="section-icon"><i className="ri-building-line"></i></span>
                    <h6>Employeur (CMS)</h6>
                </div>
                <Alert variant="info" className="alert-modern small">
                    <i className="ri-information-line me-2"></i>
                    L'employeur est <strong>CONSULTING MAXI SERVICES (C.M.S)</strong>.
                    Le <strong>client / donneur d'ordre</strong> (chez qui l'employé est mis à disposition)
                    se sélectionne à l'<strong>étape 4</strong>.
                </Alert>
            </Col>

            <Col md={8}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Raison sociale <span className="text-danger">*</span></Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-building-2-line"></i></InputGroup.Text>
                        <Form.Control name="employeur.raison_sociale" value={form.employeur.raison_sociale} onChange={handleChange} required />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Forme juridique</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-bank-line"></i></InputGroup.Text>
                        <Form.Control name="employeur.forme_juridique" value={form.employeur.forme_juridique} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={12}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Activité</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-tools-line"></i></InputGroup.Text>
                        <Form.Control name="employeur.activite" value={form.employeur.activite} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={12}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Adresse</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-map-pin-line"></i></InputGroup.Text>
                        <Form.Control name="employeur.adresse" value={form.employeur.adresse} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Téléphone 1</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-phone-line"></i></InputGroup.Text>
                        <Form.Control name="employeur.telephone" value={form.employeur.telephone} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Téléphone 2</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-smartphone-line"></i></InputGroup.Text>
                        <Form.Control name="employeur.telephone2" value={form.employeur.telephone2} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Email</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                        <Form.Control name="employeur.email" value={form.employeur.email} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Représentant <span className="text-danger">*</span></Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-user-star-line"></i></InputGroup.Text>
                        <Form.Control name="employeur.representant_nom" value={form.employeur.representant_nom} onChange={handleChange} required />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Qualité du représentant</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-briefcase-4-line"></i></InputGroup.Text>
                        <Form.Control name="employeur.representant_qualite" value={form.employeur.representant_qualite} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>

            <Col md={12}>
                <div className="section-title mt-3">
                    <span className="section-icon"><i className="ri-file-shield-line"></i></span>
                    <h6>Identifiants légaux</h6>
                </div>
            </Col>
            {[
                { key: 'rccm',     label: 'RCCM',      icon: 'ri-file-list-line' },
                { key: 'nif',      label: 'NIF',       icon: 'ri-hashtag' },
                { key: 'cnss',     label: 'N° CNSS',   icon: 'ri-shield-user-line' },
                { key: 'cnamgs',   label: 'N° CNAMGS', icon: 'ri-heart-pulse-line' },
                { key: 'ascoma',   label: 'N° ASCOMA', icon: 'ri-shield-check-line' },
                { key: 'site_web', label: 'Site web',  icon: 'ri-global-line' }
            ].map(f => (
                <Col md={4} key={f.key}>
                    <Form.Group>
                        <Form.Label className="form-label-modern">{f.label}</Form.Label>
                        <InputGroup className="input-modern">
                            <InputGroup.Text><i className={f.icon}></i></InputGroup.Text>
                            <Form.Control name={`employeur.${f.key}`} value={form.employeur[f.key]} onChange={handleChange} />
                        </InputGroup>
                    </Form.Group>
                </Col>
            ))}
        </Row>
    );

    /* ----- ÉTAPE 3 : EMPLOYÉ ----- */
    const renderStep3 = () => (
        <Row className="g-4">
            {/* Sélecteur employé */}
            <Col md={12}>
                <div className="section-title">
                    <span className="section-icon"><i className="ri-user-search-line"></i></span>
                    <h6>Sélection de l'employé</h6>
                </div>

                {selectedEmployeId ? (
                    <div className="selected-employe-card">
                        <div className="d-flex align-items-center gap-3 flex-grow-1">
                            <div className="selected-employe-avatar">
                                {form.employe.prenom?.charAt(0)}{form.employe.nom?.charAt(0)}
                            </div>
                            <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                    <span className="fw-bold" style={{ color: COLORS.primaryDark, fontSize: '1rem' }}>
                                        {form.employe.prenom} {form.employe.nom}
                                    </span>
                                    <span className="linked-badge">
                                        <i className="ri-link"></i> Lié (ID: {selectedEmployeId})
                                    </span>
                                </div>
                                <div className="d-flex flex-wrap gap-3 text-muted small">
                                    {form.employe.email && (
                                        <span><i className="ri-mail-line me-1"></i>{form.employe.email}</span>
                                    )}
                                    {form.employe.tel_personnel && (
                                        <span><i className="ri-phone-line me-1"></i>{form.employe.tel_personnel}</span>
                                    )}
                                    {form.employe.cnss && (
                                        <span><i className="ri-shield-user-line me-1"></i>CNSS: {form.employe.cnss}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <Button type="button" className="btn-modern-outline" onClick={() => setShowEmployeModal(true)}>
                                <i className="ri-exchange-line me-1"></i> Changer
                            </Button>
                            <Button type="button" className="btn-modern-outline text-danger" onClick={handleClearEmploye}>
                                <i className="ri-link-unlink me-1"></i> Détacher
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="employe-selector-actions">
                        <div className="row g-3">
                            <div className="col-lg-6">
                                <button
                                    type="button"
                                    className={`employe-selector-btn ${employeMode === 'existing' ? 'active' : ''}`}
                                    onClick={() => setShowEmployeModal(true)}
                                >
                                    <div className="selector-icon">
                                        <i className="ri-user-search-line"></i>
                                    </div>
                                    <div className="selector-content">
                                        <div className="selector-title">Sélectionner un employé existant</div>
                                        <div className="selector-desc">Choisir parmi {employes.length} employé(s)</div>
                                    </div>
                                    <i className="ri-arrow-right-s-line selector-arrow"></i>
                                </button>
                            </div>
                            <div className="col-lg-6">
                                <button
                                    type="button"
                                    className={`employe-selector-btn ${employeMode === 'manual' ? 'active' : ''}`}
                                    onClick={() => { setEmployeMode('manual'); handleClearEmploye(); }}
                                >
                                    <div className="selector-icon">
                                        <i className="ri-user-add-line"></i>
                                    </div>
                                    <div className="selector-content">
                                        <div className="selector-title">Saisie manuelle</div>
                                        <div className="selector-desc">Remplir les informations manuellement</div>
                                    </div>
                                    <i className="ri-arrow-right-s-line selector-arrow"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {selectedEmployeId && (
                    <Alert variant="info" className="alert-modern small mt-3 mb-0">
                        <i className="ri-information-line me-2"></i>
                        Les informations ci-dessous sont pré-remplies depuis la fiche employé.
                        L'ID <strong>#{selectedEmployeId}</strong> sera associé au contrat.
                    </Alert>
                )}
            </Col>

            {/* Identité */}
            <Col md={12}>
                <div className="section-title mt-3">
                    <span className="section-icon"><i className="ri-user-line"></i></span>
                    <h6>Identité de l'employé</h6>
                </div>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Nom <span className="text-danger">*</span></Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                        <Form.Control name="employe.nom" value={form.employe.nom} onChange={handleChange} required />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Prénom <span className="text-danger">*</span></Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-user-smile-line"></i></InputGroup.Text>
                        <Form.Control name="employe.prenom" value={form.employe.prenom} onChange={handleChange} required />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Date de naissance <span className="text-danger">*</span></Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-calendar-event-line"></i></InputGroup.Text>
                        <Form.Control type="date" name="employe.date_naissance" value={form.employe.date_naissance} onChange={handleChange} required />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Lieu de naissance</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-map-pin-2-line"></i></InputGroup.Text>
                        <Form.Control name="employe.lieu_naissance" value={form.employe.lieu_naissance} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Nationalité</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-flag-line"></i></InputGroup.Text>
                        <Form.Control name="employe.nationalite" value={form.employe.nationalite} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Situation familiale</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-heart-line"></i></InputGroup.Text>
                        <Form.Select name="employe.situation_familiale" value={form.employe.situation_familiale} onChange={handleChange}>
                            <option>Célibataire</option>
                            <option>Marié(e)</option>
                            <option>Divorcé(e)</option>
                            <option>Veuf(ve)</option>
                        </Form.Select>
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Nombre d'enfants</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-parent-line"></i></InputGroup.Text>
                        <Form.Control type="number" min="0" name="employe.nombre_enfants" value={form.employe.nombre_enfants} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">N° CNSS</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-shield-user-line"></i></InputGroup.Text>
                        <Form.Control name="employe.cnss" value={form.employe.cnss} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Pièce d'identité</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-id-card-line"></i></InputGroup.Text>
                        <Form.Control name="employe.piece_identite" value={form.employe.piece_identite} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Domicile</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-home-4-line"></i></InputGroup.Text>
                        <Form.Control name="employe.domicile" value={form.employe.domicile} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Nom du père</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-user-2-line"></i></InputGroup.Text>
                        <Form.Control name="employe.nom_pere" value={form.employe.nom_pere} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Nom de la mère</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-user-3-line"></i></InputGroup.Text>
                        <Form.Control name="employe.nom_mere" value={form.employe.nom_mere} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>

            {/* Contact */}
            <Col md={12}>
                <div className="section-title mt-3">
                    <span className="section-icon"><i className="ri-contacts-line"></i></span>
                    <h6>Contact & personne à prévenir</h6>
                </div>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Personne à prévenir</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-user-heart-line"></i></InputGroup.Text>
                        <Form.Control name="employe.personne_prevenir" value={form.employe.personne_prevenir} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Tél. personne à prévenir</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-phone-line"></i></InputGroup.Text>
                        <Form.Control name="employe.tel_prevenir" value={form.employe.tel_prevenir} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Tél. personnel</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-smartphone-line"></i></InputGroup.Text>
                        <Form.Control name="employe.tel_personnel" value={form.employe.tel_personnel} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Email</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                        <Form.Control type="email" name="employe.email" value={form.employe.email} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Date d'entrée</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-login-circle-line"></i></InputGroup.Text>
                        <Form.Control type="date" name="employe.date_entree" value={form.employe.date_entree} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
        </Row>
    );

    /* ----- ÉTAPE 4 : POSTE + CLIENT ----- */
    const renderStep4 = () => (
        <Row className="g-4">
            <Col md={12}>
                <div className="section-title">
                    <span className="section-icon"><i className="ri-briefcase-line"></i></span>
                    <h6>Qualification & affectation</h6>
                </div>
            </Col>

            {/* ⭐ CLIENT / DONNEUR D'ORDRE */}
          {/* ⭐ CLIENT / DONNEUR D'ORDRE */}
<Col md={12}>
    <Form.Group>
        <Form.Label className="form-label-modern">
            <i className="ri-building-4-line me-1" style={{ color: COLORS.secondary }}></i>
            Client / Donneur d'ordre <span className="text-danger">*</span>
            {selectedClientId && (
                <span className="linked-badge ms-2" style={{ background: COLORS.success }}>
                    <i className="ri-link"></i> ID #{selectedClientId}
                </span>
            )}
            {clientFromUrl && (
                <span className="linked-badge ms-2" style={{ background: COLORS.info }}>
                    <i className="ri-information-line"></i> Pré-sélectionné
                </span>
            )}
        </Form.Label>

        {/* ⭐ CARTE CLIENT PRÉ-SÉLECTIONNÉ */}
        {clientFromUrl && selectedClientId && (() => {
            const clientObj = clients.find(c => c.id_client === selectedClientId);
            return (
                <div className="selected-client-card mb-3">
                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                        <div className="selected-client-avatar">
                            <i className="ri-building-4-line"></i>
                        </div>
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                <span className="fw-bold" style={{ color: COLORS.primaryDark, fontSize: '1.05rem' }}>
                                    {clientObj?.raison_sociale || 'Client'}
                                </span>
                                {clientObj?.code_client && (
                                    <span className="badge-matricule">
                                        <i className="ri-hashtag"></i> {clientObj.code_client}
                                    </span>
                                )}
                            </div>
                            <div className="d-flex flex-wrap gap-3 text-muted small">
                                {clientObj?.telephone && (
                                    <span><i className="ri-phone-line me-1"></i>{clientObj.telephone}</span>
                                )}
                                {clientObj?.email && (
                                    <span><i className="ri-mail-line me-1"></i>{clientObj.email}</span>
                                )}
                                {clientObj?.adresse && (
                                    <span><i className="ri-map-pin-line me-1"></i>{clientObj.adresse}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            type="button"
                            className="btn-modern-outline"
                            onClick={() => navigate(`/ventes/clients/${selectedClientId}`)}
                        >
                            <i className="ri-external-link-line me-1"></i> Fiche client
                        </Button>
                        <Button
                            type="button"
                            className="btn-modern-outline text-danger"
                            onClick={handleClearClient}
                        >
                            <i className="ri-link-unlink me-1"></i> Détacher
                        </Button>
                    </div>
                </div>
            );
        })()}

        {/* Select classique si pas pré-sélectionné */}
        {!clientFromUrl && (
            <>
                <InputGroup className="input-modern">
                    <InputGroup.Text><i className="ri-building-4-line"></i></InputGroup.Text>
                    <Form.Select
                        name="poste.client"
                        value={selectedClientId || ''}
                        onChange={handleClientChange}
                        disabled={loadingClients}
                        required
                    >
                        <option value="">
                            {loadingClients
                                ? '— Chargement des clients...'
                                : '— Sélectionner un client / donneur d\'ordre —'}
                        </option>
                        {clients.map(c => (
                            <option key={c.id_client} value={c.id_client}>
                                {c.raison_sociale}{c.code_client ? ` (${c.code_client})` : ''}
                            </option>
                        ))}
                    </Form.Select>
                </InputGroup>
                <Form.Text className="text-muted small">
                    <i className="ri-information-line me-1"></i>
                    {clients.length} client(s) enregistré(s) — l'employé sera mis à disposition chez ce client.
                </Form.Text>
            </>
        )}
    </Form.Group>
</Col>

            {/* Qualification + Fonction */}
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Qualification <span className="text-danger">*</span></Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-award-line"></i></InputGroup.Text>
                        <Form.Control name="poste.qualification" value={form.poste.qualification} onChange={handleChange} required />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Fonction <span className="text-danger">*</span></Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-user-settings-line"></i></InputGroup.Text>
                        <Form.Control name="poste.fonction" value={form.poste.fonction} onChange={handleChange} required />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Département</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-node-tree"></i></InputGroup.Text>
                        <Form.Control name="poste.departement" value={form.poste.departement} onChange={handleChange} placeholder="Ex: CMS PERENCO" />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Site d'affectation</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-map-pin-line"></i></InputGroup.Text>
                        <Form.Control name="poste.site" value={form.poste.site} onChange={handleChange} placeholder="Ex: BATANGA" />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Province</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-map-2-line"></i></InputGroup.Text>
                        <Form.Control name="poste.province" value={form.poste.province} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>

            <Col md={12}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Missions principales</Form.Label>
                    {form.poste.missions.map((m, i) => (
                        <div key={i} className="d-flex gap-2 mb-2">
                            <InputGroup className="input-modern flex-grow-1">
                                <InputGroup.Text><i className="ri-checkbox-circle-line"></i></InputGroup.Text>
                                <Form.Control
                                    value={m}
                                    onChange={(e) => {
                                        const arr = [...form.poste.missions];
                                        arr[i] = e.target.value;
                                        updateForm('poste.missions', arr);
                                    }}
                                />
                            </InputGroup>
                            <Button
                                type="button"
                                variant="outline-danger"
                                className="btn-modern-outline"
                                onClick={() => updateForm('poste.missions', form.poste.missions.filter((_, idx) => idx !== i))}
                            >
                                <i className="ri-delete-bin-line"></i>
                            </Button>
                        </div>
                    ))}
                    <Button
                        type="button"
                        variant="outline-primary"
                        className="btn-modern-outline mt-2"
                        onClick={() => updateForm('poste.missions', [...form.poste.missions, ''])}
                    >
                        <i className="ri-add-line me-1"></i> Ajouter une mission
                    </Button>
                </Form.Group>
            </Col>
        </Row>
    );

    /* ----- ÉTAPE 5 : PÉRIODE ----- */
    const renderStep5 = () => (
        <Row className="g-4">
            <Col md={12}>
                <div className="section-title">
                    <span className="section-icon"><i className="ri-calendar-line"></i></span>
                    <h6>Période du contrat</h6>
                </div>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Date de début <span className="text-danger">*</span></Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-calendar-check-line"></i></InputGroup.Text>
                        <Form.Control type="date" name="periode.date_debut" value={form.periode.date_debut} onChange={handleChange} required />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="form-label-modern">
                        Date de fin {form.type_contrat === 'CDD' && <span className="text-danger">*</span>}
                    </Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-calendar-close-line"></i></InputGroup.Text>
                        <Form.Control type="date" name="periode.date_fin" value={form.periode.date_fin} onChange={handleChange} />
                    </InputGroup>
                    <Form.Text className="text-muted small">Laisser vide pour un CDI</Form.Text>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Durée totale (jours)</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-timer-line"></i></InputGroup.Text>
                        <Form.Control type="number" name="periode.duree_jours" value={form.periode.duree_jours} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Jours sur site</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-building-line"></i></InputGroup.Text>
                        <Form.Control type="number" name="periode.jours_site" value={form.periode.jours_site} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Jours de sortie</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-logout-box-line"></i></InputGroup.Text>
                        <Form.Control type="number" name="periode.jours_sortie" value={form.periode.jours_sortie} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={12}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Type de rémunération</Form.Label>
                    <div className="d-flex gap-3 flex-wrap">
                        {[
                            { v: 'site',         label: 'Sur site',      icon: 'ri-building-2-line' },
                            { v: 'recuperation', label: 'Récupération',  icon: 'ri-rest-time-line' }
                        ].map(o => (
                            <div
                                key={o.v}
                                className={`chip-radio ${form.periode.type_remuneration === o.v ? 'active' : ''}`}
                                onClick={() => updateForm('periode.type_remuneration', o.v)}
                            >
                                <i className={o.icon}></i> {o.label}
                            </div>
                        ))}
                    </div>
                </Form.Group>
            </Col>
        </Row>
    );

    /* ----- ÉTAPE 6 : RÉMUNÉRATION ----- */
    const renderStep6 = () => {
        const primes = [
            { key: 'salaire_base',      label: 'Salaire de base',      icon: 'ri-money-cfa-circle-line', suffix: '/28 Jours' },
            { key: 'prime_assiduite',   label: 'Prime d\'assiduité',   icon: 'ri-calendar-check-line',   suffix: '/M' },
            { key: 'prime_securite',    label: 'Prime de sécurité',    icon: 'ri-shield-check-line',     suffix: '/M' },
            { key: 'prime_chantier',    label: 'Prime de chantier',    icon: 'ri-tools-line',            suffix: '' },
            { key: 'prime_mer',         label: 'Prime de mer',         icon: 'ri-ship-line',             suffix: '/M' },
            { key: 'prime_rendement',   label: 'Prime de rendement',   icon: 'ri-line-chart-line',       suffix: '/M' },
            { key: 'prime_gaz',         label: 'Prime de gaz',         icon: 'ri-fire-line',             suffix: '/M' },
            { key: 'prime_transport',   label: 'Prime de transport',   icon: 'ri-bus-line',              suffix: '/M' },
            { key: 'indemnite_diverse', label: 'Indemnité diverse',    icon: 'ri-wallet-3-line',         suffix: '/M' },
            { key: 'prime_anciennete',  label: 'Prime d\'ancienneté',  icon: 'ri-time-line',             suffix: '/M' }
        ];
        return (
            <Row className="g-4">
                <Col md={12}>
                    <div className="section-title">
                        <span className="section-icon"><i className="ri-money-cfa-circle-line"></i></span>
                        <h6>Rémunération & primes</h6>
                    </div>
                </Col>
                {primes.map(p => (
                    <Col md={6} key={p.key}>
                        <Form.Group>
                            <Form.Label className="form-label-modern">
                                {p.label}
                                {p.suffix && <span className="text-muted small ms-1">({p.suffix.trim() || 'FCFA'})</span>}
                            </Form.Label>
                            <InputGroup className="input-modern">
                                <InputGroup.Text><i className={p.icon}></i></InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    name={`remuneration.${p.key}`}
                                    value={form.remuneration[p.key]}
                                    onChange={handleChange}
                                />
                                <InputGroup.Text>FCFA</InputGroup.Text>
                            </InputGroup>
                        </Form.Group>
                    </Col>
                ))}
                <Col md={12}>
                    <div className="total-card">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <div className="total-label">Total rémunération mensuelle estimée</div>
                                <div className="total-sublabel">Somme de tous les éléments ci-dessus</div>
                            </div>
                            <div className="total-value">
                                {totalRemuneration.toLocaleString('fr-FR')} <span>FCFA</span>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        );
    };

    /* ----- ÉTAPE 7 : CLAUSES ----- */
    const renderStep7 = () => (
        <Row className="g-4">
            <Col md={12}>
                <div className="section-title">
                    <span className="section-icon"><i className="ri-shield-check-line"></i></span>
                    <h6>Clauses & engagements</h6>
                </div>
            </Col>
            <Col md={12}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Fautes professionnelles passibles de licenciement</Form.Label>
                    <div className="clause-list">
                        {form.clauses.fautes.map((f, i) => (
                            <div key={i} className="clause-item">
                                <i className="ri-error-warning-line"></i>
                                <span>{f}</span>
                            </div>
                        ))}
                    </div>
                </Form.Group>
            </Col>
            <Col md={12}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Congés payés</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-calendar-2-line"></i></InputGroup.Text>
                        <Form.Control name="clauses.conges_payes" value={form.clauses.conges_payes} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
            <Col md={12}>
                {[
                    { key: 'clause_non_concurrence', label: 'Clause de non-concurrence (Article 34 du Code du Travail)' },
                    { key: 'soins_medicaux',         label: 'Visite médicale obligatoire avant embauche' },
                    { key: 'penalites_preventives',  label: 'Acceptation des pénalités préventives (Contrat Cadre)' }
                ].map(c => (
                    <Form.Check
                        key={c.key}
                        type="switch"
                        id={c.key}
                        className="custom-switch mb-2"
                        label={c.label}
                        checked={form.clauses[c.key]}
                        onChange={(e) => updateForm(`clauses.${c.key}`, e.target.checked)}
                    />
                ))}
            </Col>
            <Col md={12}>
                <Form.Group>
                    <Form.Label className="form-label-modern">Observations particulières</Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-sticky-note-line"></i></InputGroup.Text>
                        <Form.Control as="textarea" rows={3} name="clauses.observations" value={form.clauses.observations} onChange={handleChange} />
                    </InputGroup>
                </Form.Group>
            </Col>
        </Row>
    );

    /* ----- ÉTAPE 8 : DOCUMENTS ----- */
    const renderStep8 = () => {
        const docs = [
            { key: 'lettre_fin_contrat', label: 'Lettre de fin de contrat',  accept: '.pdf',              icon: 'ri-file-pdf-line' },
            { key: 'certificat_travail', label: 'Certificat de travail',      accept: '.pdf,.doc,.docx',   icon: 'ri-award-line' },
            { key: 'piece_identite',     label: 'Pièce d\'identité',          accept: '.pdf,.jpg,.png',    icon: 'ri-id-card-line' },
            { key: 'acte_naissance',     label: 'Acte de naissance légalisé', accept: '.pdf,.jpg,.png',    icon: 'ri-file-user-line' },
            { key: 'photo_identite',     label: 'Photo d\'identité (2 ex.)',  accept: '.jpg,.png',         icon: 'ri-image-line' }
        ];
        return (
            <Row className="g-4">
                <Col md={12}>
                    <div className="section-title">
                        <span className="section-icon"><i className="ri-attachment-line"></i></span>
                        <h6>Pièces jointes</h6>
                    </div>
                </Col>
                {docs.map(d => (
                    <Col md={6} key={d.key}>
                        <Form.Group>
                            <Form.Label className="form-label-modern">{d.label}</Form.Label>
                            <div className="file-upload-modern">
                                <InputGroup className="input-modern">
                                    <InputGroup.Text><i className={d.icon}></i></InputGroup.Text>
                                    <Form.Control type="file" accept={d.accept} onChange={(e) => handleFileChange(e, d.key)} />
                                </InputGroup>
                            </div>
                            {form.documents[d.key] && (
                                <Form.Text className="text-success small">
                                    <i className="ri-check-line me-1"></i>
                                    {form.documents[d.key].name}
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                ))}
                <Col md={12}>
                    <Alert variant="info" className="alert-modern small mb-0">
                        <i className="ri-information-line me-2"></i>
                        Formats acceptés : PDF, DOC, DOCX, JPG, PNG — Taille max : 5 Mo par fichier.
                    </Alert>
                </Col>
            </Row>
        );
    };

    /* ----- ÉTAPE 9 : RÉCAPITULATIF ----- */
    const renderStep9 = () => {
        const clientObj = clients.find(c => c.id_client === selectedClientId);

        const recap = [
            { label: 'Type',         value: getTypeContrat(),                                        icon: 'ri-file-list-3-line' },
            { label: 'Employé',      value: getFullName(),                                           icon: 'ri-user-line' },
            { label: 'ID Employé',   value: form.employe_id ? `#${form.employe_id}` : 'Saisie manuelle', icon: 'ri-link' },
            { label: 'Employeur',    value: form.employeur.raison_sociale,                           icon: 'ri-building-line' },
            { label: 'Client',       value: clientObj ? clientObj.raison_sociale : '— Non sélectionné —', icon: 'ri-building-4-line' },
            { label: 'ID Client',    value: form.id_client ? `#${form.id_client}` : '—',              icon: 'ri-link' },
            { label: 'Poste',        value: `${form.poste.qualification} — ${form.poste.fonction}`,  icon: 'ri-briefcase-line' },
            { label: 'Site',         value: form.poste.site || '—',                                  icon: 'ri-map-pin-line' },
            { label: 'Période',      value: `${form.periode.date_debut || '—'} → ${form.periode.date_fin || 'CDI'}`, icon: 'ri-calendar-line' },
            { label: 'Durée',        value: `${form.periode.duree_jours} jour(s)`,                    icon: 'ri-timer-line' },
            { label: 'Rémunération', value: `${totalRemuneration.toLocaleString('fr-FR')} FCFA`,     icon: 'ri-money-cfa-circle-line' }
        ];
        return (
            <Row className="g-4">
                <Col md={12}>
                    <div className="section-title">
                        <span className="section-icon"><i className="ri-check-double-line"></i></span>
                        <h6>Vérification finale</h6>
                    </div>
                    <Alert variant="warning" className="alert-modern">
                        <i className="ri-alert-line me-2"></i>
                        Vérifiez attentivement les informations avant validation.
                    </Alert>
                </Col>
                {recap.map((r, i) => (
                    <Col md={6} key={i}>
                        <div className="recap-card">
                            <div className="recap-icon"><i className={r.icon}></i></div>
                            <div>
                                <div className="recap-label">{r.label}</div>
                                <div className="recap-value">{r.value}</div>
                            </div>
                        </div>
                    </Col>
                ))}
                {form.clauses.observations && (
                    <Col md={12}>
                        <div className="recap-card">
                            <div className="recap-icon"><i className="ri-sticky-note-line"></i></div>
                            <div>
                                <div className="recap-label">Observations</div>
                                <div className="recap-value">{form.clauses.observations}</div>
                            </div>
                        </div>
                    </Col>
                )}
            </Row>
        );
    };

    const stepRenderers = {
        1: renderStep1, 2: renderStep2, 3: renderStep3, 4: renderStep4,
        5: renderStep5, 6: renderStep6, 7: renderStep7, 8: renderStep8, 9: renderStep9
    };

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    <FixedAlertBar alerts={fixedAlerts} onClose={removeAlert} />
                    <ToastNotification show={toast.show} onClose={hideToast} type={toast.type} title={toast.title} message={toast.message} />

                    {/* En-tête */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-file-copy-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Nouveau contrat</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-user-line"></i>
                                        {getFullName()}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-file-list-3-line"></i>
                                        {getTypeContrat()}
                                    </span>
                                    {form.employe_id && (
                                        <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-link"></i>
                                            Employé #{form.employe_id}
                                        </span>
                                    )}
                                    {form.id_client && (
                                        <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                            <i className="ri-building-4-line"></i>
                                            Client #{form.id_client}
                                        </span>
                                    )}
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-progress-4-line"></i>
                                        Étape {currentStep}/{STEPS.length}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            {clientFromUrl && form.id_client && (
                                <Button
                                    className="btn-modern-outline"
                                    onClick={() => navigate(`/ventes/clients/${form.id_client}`)}
                                >
                                    <i className="ri-arrow-left-line"></i> Fiche client
                                </Button>
                            )}
                            <Button variant="outline-secondary" size="sm" onClick={() => navigate('/rh/contrats')} className="btn-modern-outline">
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* Barre de progression */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-3 p-lg-4">
                            <div className="wizard-steps">
                                {STEPS.map((s, i) => {
                                    const isActive = currentStep === s.id;
                                    const isDone = currentStep > s.id;
                                    return (
                                        <div
                                            key={s.id}
                                            className={`wizard-step ${isActive ? 'active' : ''} ${isDone ? 'done' : ''} ${currentStep >= s.id ? 'clickable' : ''}`}
                                            onClick={() => goToStep(s.id)}
                                        >
                                            <div className="step-circle">
                                                {isDone ? <i className="ri-check-line"></i> : <i className={s.icon}></i>}
                                            </div>
                                            <div className="step-info">
                                                <div className="step-label">{s.label}</div>
                                                <div className="step-desc">{s.desc}</div>
                                            </div>
                                            {i < STEPS.length - 1 && <div className="step-line"></div>}
                                        </div>
                                    );
                                })}
                            </div>
                            <ProgressBar now={progress} className="mt-3 wizard-progress" style={{ height: 6, borderRadius: 3 }} />
                        </Card.Body>
                    </Card>

                    {/* Contenu de l'étape */}
                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <h5 className="mb-0 text-white">
                                            <i className={`${STEPS[currentStep - 1].icon} me-2`}></i>
                                            {STEPS[currentStep - 1].label} — {STEPS[currentStep - 1].desc}
                                        </h5>
                                        <Badge className="badge-header-light">
                                            <i className="ri-user-line me-1"></i>
                                            {getFullName()}
                                        </Badge>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>
                                        <div className="step-content">
                                            {stepRenderers[currentStep]()}
                                        </div>

                                        {/* Navigation */}
                                        <hr className="my-4" style={{ borderColor: '#e2e8f0' }} />
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                onClick={currentStep === 1 ? () => navigate('/rh/contrats') : goPrev}
                                                className="btn-modern-outline"
                                                disabled={saving}
                                            >
                                                <i className="ri-arrow-left-line me-1"></i>
                                                {currentStep === 1 ? 'Annuler' : 'Précédent'}
                                            </Button>

                                            <div className="d-flex gap-2 align-items-center">
                                                <span className="text-muted small d-none d-md-inline">
                                                    {currentStep} sur {STEPS.length}
                                                </span>
                                                {currentStep < STEPS.length ? (
                                                    <Button type="button" onClick={goNext} className="btn-modern-primary">
                                                        Suivant <i className="ri-arrow-right-line ms-1"></i>
                                                    </Button>
                                                ) : (
                                                    <Button type="submit" disabled={saving} className="btn-modern-primary">
                                                        {saving ? (
                                                            <><Spinner size="sm" className="me-2" /> Enregistrement...</>
                                                        ) : (
                                                            <><i className="ri-save-line me-2"></i> Enregistrer le contrat</>
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== MODALE SÉLECTION EMPLOYÉ ===== */}
            <Modal
                show={showEmployeModal}
                onHide={() => { setShowEmployeModal(false); setSearchEmploye(''); }}
                size="lg"
                centered
                className="modal-charte"
            >
                <Modal.Header closeButton className="modal-header-charte"
                    style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-user-search-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Sélectionner un employé</Modal.Title>
                            <p className="text-white-50 mb-0 small">{employes.length} employé(s) disponible(s)</p>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-0">
                    <div className="p-3 border-bottom">
                        <div className="search-box-modal">
                            <i className="ri-search-line"></i>
                            <Form.Control
                                type="text"
                                placeholder="Rechercher par nom, prénom, email, matricule..."
                                value={searchEmploye}
                                onChange={(e) => setSearchEmploye(e.target.value)}
                                className="search-input-modal"
                                autoFocus
                            />
                        </div>
                    </div>

                    <div className="employe-list-modal">
                        {loadingEmployes ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" style={{ color: COLORS.primary }} />
                                <p className="text-muted small mt-3 mb-0">Chargement des employés...</p>
                            </div>
                        ) : filteredEmployes.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="empty-state-icon-charte">
                                    <i className="ri-user-search-line"></i>
                                </div>
                                <h6 className="fw-semibold mb-1">Aucun employé trouvé</h6>
                                <p className="text-muted small mb-0">
                                    {searchEmploye ? 'Essayez un autre terme' : 'Aucun employé enregistré'}
                                </p>
                            </div>
                        ) : (
                            filteredEmployes.map(emp => (
                                <div
                                    key={emp.id}
                                    className={`employe-item-modal ${selectedEmployeId === emp.id ? 'selected' : ''}`}
                                    onClick={() => handleSelectEmploye(emp)}
                                >
                                    <div className="employe-avatar-modal">
                                        {emp.prenom?.charAt(0)}{emp.nom?.charAt(0)}
                                    </div>
                                    <div className="flex-grow-1 min-width-0">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                {emp.prenom} {emp.nom}
                                            </span>
                                            {emp.matricule && (
                                                <span className="badge-matricule">
                                                    <i className="ri-fingerprint-line"></i> {emp.matricule}
                                                </span>
                                            )}
                                        </div>
                                        <div className="d-flex flex-wrap gap-3 text-muted small mt-1">
                                            {emp.poste && <span><i className="ri-briefcase-line me-1"></i>{emp.poste}</span>}
                                            {emp.email && <span><i className="ri-mail-line me-1"></i>{emp.email}</span>}
                                            {emp.telephone && <span><i className="ri-phone-line me-1"></i>{emp.telephone}</span>}
                                        </div>
                                    </div>
                                    <div className="d-flex flex-column align-items-end gap-1">
                                        {emp.statut && (
                                            <span className={`status-pill ${emp.statut}`}>{emp.statut}</span>
                                        )}
                                        {emp.type_contrat && (
                                            <span className="type-pill">{emp.type_contrat}</span>
                                        )}
                                    </div>
                                    <i className="ri-arrow-right-s-line arrow-icon"></i>
                                </div>
                            ))
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-0 px-4 pb-3">
                    <Button className="btn-modern-outline" onClick={() => { setShowEmployeModal(false); setSearchEmploye(''); }}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== Header ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .badge-header-light {
                    background: rgba(255,255,255,0.2); color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px; padding: 0.4rem 1rem; font-weight: 500;
                }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                }

                /* ===== Wizard sur une seule ligne ===== */
                .wizard-steps {
                    display: flex;
                    flex-wrap: nowrap;
                    gap: 0.35rem;
                    align-items: center;
                    overflow-x: auto;
                    padding-bottom: 0.4rem;
                    scrollbar-width: thin;
                }
                .wizard-steps::-webkit-scrollbar { height: 4px; }
                .wizard-steps::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }

                .wizard-step {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    flex: 1 1 0;
                    min-width: 0;
                    padding: 0.5rem 0.4rem;
                    border-radius: 10px;
                    transition: all 0.25s ease;
                    opacity: 0.55;
                    white-space: nowrap;
                }
                .wizard-step.clickable { cursor: pointer; }
                .wizard-step.clickable:hover { background: #f8fafc; opacity: 0.85; }
                .wizard-step.active {
                    background: linear-gradient(135deg, ${COLORS.primary}10, ${COLORS.secondary}10);
                    opacity: 1;
                }
                .wizard-step.done { opacity: 0.9; }

                .step-circle {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #e2e8f0;
                    color: #64748b;
                    font-size: 0.85rem;
                    font-weight: 700;
                    transition: all 0.25s ease;
                    flex-shrink: 0;
                }
                .wizard-step.active .step-circle {
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    color: white;
                    box-shadow: 0 4px 12px ${COLORS.primary}40;
                    transform: scale(1.1);
                }
                .wizard-step.done .step-circle {
                    background: var(--success);
                    color: white;
                }
                .step-info { min-width: 0; overflow: hidden; }
                .step-label {
                    font-size: 0.72rem;
                    font-weight: 700;
                    color: #475569;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                }
                .wizard-step.active .step-label { color: var(--primary-dark); }
                .step-desc {
                    font-size: 0.62rem;
                    color: #94a3b8;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                }
                .wizard-progress .progress-bar {
                    background: linear-gradient(90deg, var(--primary), var(--secondary)) !important;
                    transition: width 0.4s ease;
                }
                @media (max-width: 992px) {
                    .step-desc { display: none; }
                    .wizard-step { padding: 0.4rem 0.3rem; gap: 0.35rem; }
                    .step-circle { width: 28px; height: 28px; font-size: 0.75rem; }
                    .step-label { font-size: 0.68rem; }
                }

                /* ===== Sections ===== */
                .section-title {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title h6 { margin: 0; color: var(--primary-dark); font-weight: 600; font-size: 1rem; }
                .section-icon {
                    width: 32px; height: 32px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    border-radius: 8px; display: inline-flex;
                    align-items: center; justify-content: center;
                    color: white; font-size: 0.95rem;
                    box-shadow: 0 2px 6px rgba(41, 90, 167, 0.2);
                }

                /* ===== Inputs ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    font-size: 1rem; border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                    border-left: 2px solid var(--secondary);
                }

                /* ===== Boutons ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.45rem 1.3rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33, 172, 205, 0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white;
                    padding: 0.55rem 2rem; border-radius: 50px;
                    font-weight: 600; font-size: 0.9rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.3);
                }
                .btn-modern-primary:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41, 90, 167, 0.4);
                    color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.7; }

                /* ===== Type selector ===== */
                .type-selector {
                    display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 1rem;
                }
                .type-card {
                    background: white; border: 2px solid #e2e8f0;
                    border-radius: 14px; padding: 1.2rem 1rem;
                    cursor: pointer; position: relative;
                    transition: all 0.25s ease; text-align: center;
                }
                .type-card:hover {
                    border-color: ${COLORS.secondary}80;
                    transform: translateY(-3px);
                    box-shadow: 0 8px 20px rgba(33, 172, 205, 0.15);
                }
                .type-card.active {
                    border-color: var(--primary);
                    background: linear-gradient(135deg, ${COLORS.primary}08, ${COLORS.secondary}08);
                    box-shadow: 0 8px 20px ${COLORS.primary}25;
                }
                .type-icon {
                    width: 52px; height: 52px; border-radius: 14px;
                    margin: 0 auto 0.7rem;
                    display: flex; align-items: center; justify-content: center;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    color: white; font-size: 1.5rem;
                }
                .type-title { font-weight: 700; color: #1e293b; font-size: 0.95rem; }
                .type-desc { font-size: 0.75rem; color: #94a3b8; margin-top: 0.2rem; }
                .type-check {
                    position: absolute; top: 10px; right: 10px;
                    width: 24px; height: 24px; border-radius: 50%;
                    background: var(--success); color: white;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 0.85rem;
                }

                /* ===== Chips ===== */
                .chip-radio {
                    display: inline-flex; align-items: center; gap: 0.5rem;
                    padding: 0.6rem 1.2rem; border-radius: 50px;
                    border: 2px solid #e2e8f0; background: white;
                    cursor: pointer; font-size: 0.85rem; font-weight: 500;
                    color: #475569; transition: all 0.2s ease;
                }
                .chip-radio:hover { border-color: ${COLORS.secondary}80; }
                .chip-radio.active {
                    border-color: var(--primary);
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    color: white;
                    box-shadow: 0 4px 12px ${COLORS.primary}40;
                }

                /* ===== Total card ===== */
                .total-card {
                    background: linear-gradient(135deg, ${COLORS.primary}08, ${COLORS.secondary}08);
                    border: 2px dashed ${COLORS.primary}40;
                    border-radius: 14px; padding: 1.2rem 1.5rem;
                }
                .total-label { font-weight: 700; color: var(--primary-dark); font-size: 0.95rem; }
                .total-sublabel { color: #94a3b8; font-size: 0.75rem; }
                .total-value {
                    font-size: 1.8rem; font-weight: 800;
                    color: var(--primary);
                    letter-spacing: -0.5px;
                }
                .total-value span { font-size: 0.9rem; color: #64748b; font-weight: 500; }

                /* ===== Clauses ===== */
                .clause-list {
                    background: #fef2f2; border-radius: 12px;
                    padding: 1rem 1.2rem; border-left: 4px solid var(--danger);
                }
                .clause-item {
                    display: flex; align-items: flex-start; gap: 0.6rem;
                    padding: 0.4rem 0; color: #7f1d1d; font-size: 0.85rem;
                }
                .clause-item i { color: var(--danger); margin-top: 2px; }
                .custom-switch .form-check-input:checked {
                    background-color: var(--primary); border-color: var(--primary);
                }

                /* ===== Recap ===== */
                .recap-card {
                    display: flex; align-items: center; gap: 1rem;
                    background: #f8fafc; border-radius: 12px;
                    padding: 1rem 1.2rem; border-left: 4px solid var(--secondary);
                    transition: all 0.2s ease;
                }
                .recap-card:hover { background: #f1f5f9; transform: translateX(3px); }
                .recap-icon {
                    width: 44px; height: 44px; border-radius: 12px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    color: white; display: flex; align-items: center;
                    justify-content: center; font-size: 1.2rem; flex-shrink: 0;
                }
                .recap-label { font-size: 0.72rem; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
                .recap-value { font-weight: 600; color: #1e293b; font-size: 0.95rem; }

                /* ===== Alertes ===== */
                .alert-modern {
                    border: none; border-radius: 12px;
                    padding: 1rem 1.25rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
                    border-left: 4px solid;
                }
                .alert-modern.alert-success { border-left-color: var(--success); background: #f0fdf4; }
                .alert-modern.alert-danger  { border-left-color: var(--danger);  background: #fef2f2; }
                .alert-modern.alert-warning { border-left-color: var(--warning); background: #fffbeb; }
                .alert-modern.alert-info    { border-left-color: var(--info);    background: #eff6ff; }
                .alert-icon-wrapper {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    background: rgba(255,255,255,0.6);
                }

                /* ===== Toast ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px; display: flex;
                    align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer;
                    opacity: 0.8; transition: opacity 0.2s;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== Fixed alert bar ===== */
                .fixed-alert-bar {
                    position: sticky; top: 70px; z-index: 1000; margin-bottom: 1rem;
                }
                .fixed-alert-bar .alert { animation: slideInDown 0.3s ease-out; }

                /* ===== File upload ===== */
                .file-upload-modern .form-control[type="file"] { padding: 0.45rem 0.9rem; cursor: pointer; }
                .file-upload-modern .form-control[type="file"]::file-selector-button {
                    background: #f1f5f9; border: none; border-radius: 6px;
                    padding: 0.3rem 0.8rem; margin-right: 0.8rem;
                    color: var(--primary); font-weight: 500; font-size: 0.8rem;
                    transition: background 0.2s;
                }
                .file-upload-modern .form-control[type="file"]::file-selector-button:hover {
                    background: #e2e8f0;
                }

                .step-content { animation: fadeInUp 0.35s ease-out; }

                /* ===== SÉLECTEUR EMPLOYÉ ===== */
                .employe-selector-actions { display: flex; flex-direction: column; gap: 0.75rem; }
                .employe-selector-btn {
                    display: flex; align-items: center; gap: 1rem;
                    background: white; border: 2px dashed #e2e8f0;
                    border-radius: 14px; padding: 1rem 1.25rem;
                    cursor: pointer; text-align: left;
                    transition: all 0.25s ease; width: 100%; height: 100%;
                }
                .employe-selector-btn:hover {
                    border-color: var(--secondary);
                    background: linear-gradient(135deg, ${COLORS.primary}05, ${COLORS.secondary}05);
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(33,172,205,0.12);
                }
                .employe-selector-btn.active {
                    border-style: solid;
                    border-color: var(--primary);
                    background: linear-gradient(135deg, ${COLORS.primary}08, ${COLORS.secondary}08);
                }
                .selector-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-size: 1.4rem; flex-shrink: 0;
                }
                .selector-content { flex: 1; min-width: 0; }
                .selector-title { font-weight: 700; color: #1e293b; font-size: 0.95rem; margin-bottom: 0.2rem; }
                .selector-desc { font-size: 0.78rem; color: #94a3b8; }
                .selector-arrow { font-size: 1.5rem; color: #cbd5e1; transition: all 0.25s ease; }
                .employe-selector-btn:hover .selector-arrow { color: var(--secondary); transform: translateX(4px); }

                /* ===== CARTE LIÉE ===== */
                .selected-employe-card {
                    display: flex; align-items: center; justify-content: space-between;
                    gap: 1rem; flex-wrap: wrap;
                    background: linear-gradient(135deg, ${COLORS.primary}08, ${COLORS.secondary}08);
                    border: 2px solid ${COLORS.primary}40;
                    border-radius: 14px; padding: 1.25rem 1.5rem;
                }
                .selected-employe-avatar {
                    width: 56px; height: 56px; border-radius: 14px;
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 1.2rem;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    flex-shrink: 0;
                }
                .linked-badge {
                    background: var(--success); color: white;
                    padding: 0.15rem 0.6rem; border-radius: 50px;
                    font-size: 0.65rem; font-weight: 700;
                    display: inline-flex; align-items: center; gap: 4px;
                }

                /* ===== MODALE ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .search-box-modal { position: relative; }
                .search-box-modal > i {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1.1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-modal {
                    border: 2px solid #e2e8f0 !important;
                    border-radius: 50px !important;
                    padding: 0.65rem 1rem 0.65rem 2.85rem !important;
                    font-size: 0.9rem;
                }
                .search-input-modal:focus {
                    border-color: var(--secondary) !important;
                    box-shadow: 0 0 0 3px ${COLORS.secondary}15 !important;
                }
                .employe-list-modal { max-height: 480px; overflow-y: auto; padding: 0.5rem; }
                .employe-item-modal {
                    display: flex; align-items: center; gap: 1rem;
                    padding: 0.9rem 1rem; border-radius: 12px;
                    cursor: pointer; transition: all 0.2s ease;
                    border: 2px solid transparent;
                }
                .employe-item-modal:hover {
                    background: #f8fafc;
                    border-color: ${COLORS.secondary}40;
                }
                .employe-item-modal.selected {
                    background: linear-gradient(135deg, ${COLORS.primary}10, ${COLORS.secondary}10);
                    border-color: var(--primary);
                }
                .employe-avatar-modal {
                    width: 46px; height: 46px; border-radius: 12px;
                    background: linear-gradient(135deg, ${COLORS.primary}20, ${COLORS.secondary}20);
                    color: var(--primary); font-weight: 700; font-size: 0.95rem;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .min-width-0 { min-width: 0; }
                .badge-matricule {
                    background: #f1f5f9; color: #64748b;
                    padding: 0.1rem 0.5rem; border-radius: 50px;
                    font-size: 0.65rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 3px;
                }
                .status-pill {
                    padding: 0.15rem 0.6rem; border-radius: 50px;
                    font-size: 0.65rem; font-weight: 700;
                    text-transform: uppercase; letter-spacing: 0.3px;
                }
                .status-pill.actif { background: ${COLORS.success}15; color: var(--success); }
                .status-pill.inactif { background: ${COLORS.danger}15; color: var(--danger); }
                .status-pill.suspendu { background: ${COLORS.warning}15; color: var(--warning); }
                .type-pill {
                    background: #f1f5f9; color: #475569;
                    padding: 0.15rem 0.5rem; border-radius: 50px;
                    font-size: 0.6rem; font-weight: 600;
                }
                .arrow-icon { font-size: 1.5rem; color: #cbd5e1; transition: all 0.2s ease; }
                .employe-item-modal:hover .arrow-icon { color: var(--secondary); transform: translateX(3px); }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== Animations ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes slideInDown {
                    from { opacity: 0; transform: translateY(-100%); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to   { width: 0%; }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(15px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .rounded-3 { border-radius: 12px !important; }

                /* ===== Responsive ===== */
                @media (max-width: 768px) {
                    .wizard-step { flex: 1 1 45%; }
                    .step-desc { display: none; }
                    .total-value { font-size: 1.4rem; }
                    .header-modern { padding: 1rem; }
                    .header-title { font-size: 1.1rem; }
                    .selected-employe-card { flex-direction: column; align-items: stretch; }
                    .employe-list-modal { max-height: 60vh; }
                }
                @media (max-width: 480px) {
                    .wizard-step { flex: 1 1 100%; }
                    .type-selector { grid-template-columns: 1fr 1fr; }
                }
            `}</style>
        </>
    );
}